import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { chartDataService } from '../services/chartDataService';
import { getChartCategory, setChartCategory as setStoredChartCategory, CHART_CONFIG_CACHE_KEYS, DEFAULT_CHART_CONFIG, USE_CHART_DB, MASTER_VIDEO_LIST_FALLBACK_ID } from '../config';

// Storage keys for caching discovered IDs.
// NOTE: DB mode and Sheets mode cache DIFFERENT shapes under these keys
// (e.g. chartFolders maps name->folderId in Sheets mode but name->name in DB
// mode; chartConfig has different provenance). To stop a stale cache from one
// mode bleeding into the other when USE_CHART_DB is toggled, every key is
// suffixed per mode via cacheKey() below.
const CACHE_KEY_BASES = {
  MASTER_VIDEO_LIST_ID: 'billboard_masterVideoListId',
  CHART_FOLDERS: 'billboard_chartFolders', // JSON object mapping category name -> folder ID
};
// Suffix cache keys by mode so DB-mode and Sheets-mode caches never collide.
const CACHE_MODE_SUFFIX = USE_CHART_DB ? '__db' : '';
const cacheKey = (base: string): string => `${base}${CACHE_MODE_SUFFIX}`;
const CACHE_KEYS = {
  MASTER_VIDEO_LIST_ID: cacheKey(CACHE_KEY_BASES.MASTER_VIDEO_LIST_ID),
  CHART_FOLDERS: cacheKey(CACHE_KEY_BASES.CHART_FOLDERS),
  CHART_CONFIG: cacheKey(CHART_CONFIG_CACHE_KEYS.CHART_CONFIG),
  CHART_CONFIG_SHEET_ID: cacheKey(CHART_CONFIG_CACHE_KEYS.CHART_CONFIG_SHEET_ID),
};

export interface ChartConfigEntry {
  chartName: string;
  horizontalTemplateId: string;
  verticalTemplateId: string;
  voSubfolder: string;
  chartId: string;
  defaultOutroText: string;
  themeColor: string;
  bilingual?: boolean; // when true, show the EN/ES VO language toggle (DB charts table)
}

export type ChartConfigMap = { [chartName: string]: ChartConfigEntry };

interface ChartFolderMap {
  [categoryName: string]: string; // Maps category name (e.g., "Hot 100") to folder ID
}

interface GoogleSheetsContextValue {
  // Discovery state
  isLoading: boolean;
  error: string | null;

  // Discovered IDs
  masterVideoListId: string | null;
  chartCategoryFolderId: string | null;

  // Chart category management
  chartCategory: string;
  availableChartCategories: string[];
  setChartCategory: (category: string) => void;


  // Chart config from Chart Config sheet
  chartConfig: ChartConfigMap;
  currentChartConfig: ChartConfigEntry | null;

  // Re-discovery function (for manual refresh)
  rediscover: () => Promise<void>;
}

const GoogleSheetsContext = createContext<GoogleSheetsContextValue | null>(null);

interface GoogleSheetsProviderProps {
  children: ReactNode;
}

export const GoogleSheetsProvider: React.FC<GoogleSheetsProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [masterVideoListId, setMasterVideoListId] = useState<string | null>(null);
  const [chartFolders, setChartFolders] = useState<ChartFolderMap>({});
  const [chartCategory, setChartCategoryState] = useState<string>(getChartCategory());
  const [chartConfig, setChartConfig] = useState<ChartConfigMap>(() => {
    const cached = localStorage.getItem(CACHE_KEYS.CHART_CONFIG);
    if (cached) {
      try { return JSON.parse(cached); } catch (e) { /* ignore */ }
    }
    return {};
  });

  // Ref to prevent infinite discovery loops
  const hasDiscoveredRef = useRef(false);

  // Derived state
  const chartCategoryFolderId = chartFolders[chartCategory] || null;
  const availableChartCategories = Object.keys(chartFolders);
  const currentChartConfig: ChartConfigEntry | null = chartConfig[chartCategory] || (
    // Fallback: if no chart config sheet found, use defaults for Hot 100
    chartCategory === DEFAULT_CHART_CONFIG.chartName
      ? DEFAULT_CHART_CONFIG
      : null
  );

  // Function to set chart category (updates both state and localStorage)
  const handleSetChartCategory = useCallback((category: string) => {
    setChartCategoryState(category);
    setStoredChartCategory(category);
  }, []);

  // Discovery: load chart config + categories from the chart-data DB.
  const discover = useCallback(async () => {
    // Prevent infinite discovery loops
    if (hasDiscoveredRef.current) {
      console.log('[GoogleSheetsContext] Discovery already completed, skipping');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // DB-backed discovery (Sheets fully retired in dev). Chart config +
      // categories come from the Postgres chart-data API. chartFolders is keyed
      // by chart NAME (value = the name) only so chartCategoryFolderId is truthy;
      // the weekly-sheet picker lists by chart name via chartDataService. The
      // Master Video List + Chart Config now live in the DB (videos / charts
      // tables); masterVideoListId is kept as a stable non-Drive id for the few
      // vestigial consumers that still read it.
      console.log('[GoogleSheetsContext] Discovering chart config from the chart-data API');
      const { chartConfig: dbConfig, categories } = await chartDataService.getCharts();
      setChartConfig(dbConfig);
      localStorage.setItem(CACHE_KEYS.CHART_CONFIG, JSON.stringify(dbConfig));
      const folderMap: ChartFolderMap = {};
      categories.forEach(name => { folderMap[name] = name; });
      setChartFolders(folderMap);
      localStorage.setItem(CACHE_KEYS.CHART_FOLDERS, JSON.stringify(folderMap));
      if (!folderMap[chartCategory] && categories.length > 0) {
        handleSetChartCategory(categories[0]);
      }
      const masterId = localStorage.getItem(CACHE_KEYS.MASTER_VIDEO_LIST_ID) || MASTER_VIDEO_LIST_FALLBACK_ID;
      setMasterVideoListId(masterId);
      localStorage.setItem(CACHE_KEYS.MASTER_VIDEO_LIST_ID, masterId);
      hasDiscoveredRef.current = true;
      setIsLoading(false);
      console.log('[GoogleSheetsContext] Discovery complete:', categories);

    } catch (err) {
      console.error('[GoogleSheetsContext] Discovery error:', err);
      setError(err instanceof Error ? err.message : 'Failed to discover Google Sheets configuration');
      setIsLoading(false);

      // Try to load from cache on error
      const cachedMasterListId = localStorage.getItem(CACHE_KEYS.MASTER_VIDEO_LIST_ID);
      const cachedChartFolders = localStorage.getItem(CACHE_KEYS.CHART_FOLDERS);

      if (cachedMasterListId) {
        console.log('[GoogleSheetsContext] Using cached Master Video List ID');
        setMasterVideoListId(cachedMasterListId);
      }

      if (cachedChartFolders) {
        try {
          console.log('[GoogleSheetsContext] Using cached Chart Folders');
          setChartFolders(JSON.parse(cachedChartFolders));
        } catch (e) {
          console.error('[GoogleSheetsContext] Failed to parse cached chart folders');
        }
      }

      const cachedChartConfig = localStorage.getItem(CACHE_KEYS.CHART_CONFIG);
      if (cachedChartConfig) {
        try {
          console.log('[GoogleSheetsContext] Using cached Chart Config');
          setChartConfig(JSON.parse(cachedChartConfig));
        } catch (e) {
          console.error('[GoogleSheetsContext] Failed to parse cached chart config');
        }
      }
    }
  }, [chartCategory, handleSetChartCategory]);

  // Force a fresh discovery (chart config + categories), bypassing the
  // once-only guard. Used after the Chart Settings page edits the charts table
  // so the new config takes effect without a page reload.
  const rediscover = useCallback(async () => {
    hasDiscoveredRef.current = false;
    await discover();
  }, [discover]);

  // Run discovery on mount
  useEffect(() => {
    discover();
  }, [discover]);

  const contextValue: GoogleSheetsContextValue = {
    isLoading,
    error,
    masterVideoListId,
    chartCategoryFolderId,
    chartCategory,
    availableChartCategories,
    setChartCategory: handleSetChartCategory,
    chartConfig,
    currentChartConfig,
    rediscover,
  };

  return (
    <GoogleSheetsContext.Provider value={contextValue}>
      {children}
    </GoogleSheetsContext.Provider>
  );
};

// Custom hook to use the context
export const useGoogleSheets = (): GoogleSheetsContextValue => {
  const context = useContext(GoogleSheetsContext);

  if (!context) {
    throw new Error('useGoogleSheets must be used within a GoogleSheetsProvider');
  }

  return context;
};

// Export context for testing purposes
export { GoogleSheetsContext };
